import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Group } from "./Group";
import { Heading } from "./Heading";
import { Stack } from "./Stack";
import { Text } from "./Text";

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: Breadcrumb[];
}

/**
 * Reemplazo de `components/layout/page-header.tsx` (Mantine · 45 usos) ·
 * título + subtítulo + acciones a la derecha, con breadcrumbs opcionales.
 * `description` acepta `ReactNode` (a veces trae un `Group` con más
 * elementos) y se renderiza en un `<div>`, no `<p>`, por la misma razón que
 * el original: anidar bloques dentro de un `<p>` es HTML inválido.
 */
export function PageHeader({ title, description, actions, breadcrumbs }: PageHeaderProps) {
  return (
    <Stack gap="xs" className="mb-6">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {breadcrumbs.map((b, i) => (
              <li key={i} className="flex items-center gap-1.5">
                {i > 0 && <span aria-hidden="true">›</span>}
                {b.href ? (
                  <Link to={b.href as never} className="hover:text-foreground hover:underline">
                    {b.label}
                  </Link>
                ) : (
                  <span>{b.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
        <Stack gap={4} className="min-w-60 flex-1">
          <Heading order={2} className="text-[22px]">
            {title}
          </Heading>
          {description && (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </Stack>
        {actions && <Group gap="xs">{actions}</Group>}
      </Group>
    </Stack>
  );
}
